import React from "react";
import { Settings, Save } from "lucide-react";
import { Button } from "../../../components/ui";
import { useWorkflowSettings } from "../hooks/useWorkflowSettings";

/**
 * WorkflowTab - Premium workflow and automation settings.
 */
export default function WorkflowTab() {
    const { settings, loading, toggleSetting, saving: isSaving } = useWorkflowSettings();

    if (loading) {
        return <div className="py-20 text-center text-slate-400 animate-pulse">Loading workflow configuration...</div>;
    }

    return (
        <div className="max-w-4xl space-y-10 animate-in fade-in duration-500 pb-20">
            {/* Header Description */}
            <div className="flex gap-5 items-start bg-slate-50/50 p-6 rounded-3xl border border-slate-100">
                <div className="w-12 h-12 bg-white shadow-sm border border-slate-100 rounded-2xl flex items-center justify-center text-brand-blue shrink-0">
                    <Settings size={24} />
                </div>
                <div className="space-y-1">
                    <h3 className="text-[17px] font-bold text-slate-800">Workflow & automations</h3>
                    <p className="text-[13px] text-slate-500 leading-relaxed max-w-2xl">
                        When enabled: (1) new material requests are auto-approved from store stock up to each requested line (same as manual approve, so partial stock yields partial approval); (2) only owners and admins can dispatch from inventory—store managers cannot dispatch until this is off. Saved on the server.
                    </p>
                </div>
            </div>

            {/* Main Settings Section */}
            <div className="space-y-6">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 rounded-full bg-orange-500 shadow-sm shadow-orange-200"></div>
                    <h4 className="text-[14px] font-bold text-slate-700 uppercase tracking-tight">Owner approval required</h4>
                </div>

                <div className="grid grid-cols-1 gap-4">
                    {/* Inventory/Material Release Toggle */}
                    <div className="flex items-center justify-between border border-slate-200/60 rounded-2xl p-6 bg-white hover:border-brand-blue/30 transition-all shadow-sm group">
                        <div className="space-y-1">
                            <p className="text-[15px] font-bold text-slate-800 leading-tight">
                                For inventory / material release
                            </p>
                            <p className="text-[13px] text-slate-500 font-medium">
                                Auto-approve new requests from available stock, and restrict dispatch to owner and admin only.
                            </p>
                        </div>

                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={settings.approvalMaterialRelease || false}
                                onChange={() => toggleSetting('approvalMaterialRelease')}
                            />
                            <div className="w-14 h-7 bg-slate-200 peer-focus:ring-4 peer-focus:ring-brand-blue/10 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-brand-blue after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all shadow-inner" />
                        </label>
                    </div>
                </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-6">
                <Button 
                    variant="orange" 
                    leftIcon={<Save size={18} />}
                    className="rounded-xl px-8 h-12 font-bold shadow-lg shadow-orange-100/50 hover:shadow-orange-200/50 transition-all"
                    onClick={() => {}} // Auto-saved on toggle, button provided for UI consistency
                >
                    Save configuration
                </Button>
            </div>

            {/* Commented out previous implementation as requested */}
            {/* 
            const sections = [
                {
                    title: "Assignment Automations",
                    color: "bg-blue-500",
                    items: [
                        { id: "autoAssignLeads", l: "Auto Assign New customers to Telecallers", d: "Incoming customers will be automatically assigned to the telecalling team." },
                        { id: "assignAppointedLeads", l: "Assign Appointed leads to Sales / Marketing team", d: "Automatically move appointed leads to the sales and marketing queue." },
                        { id: "enableLocationTracker", l: "Enable Sales Executives locations tracker", d: "Geographic Location & route tracker." },
                    ],
                },
                {
                    title: "Owner Approval Required",
                    color: "bg-amber-500",
                    grid: true,
                    items: [
                        { id: "approvalMaterialRelease", l: "For Inventory release" },
                        { id: "approvalProjectStart", l: "For Project Start (can accept without pre-payment)" },
                        { id: "approvalProjectCost", l: "Overall Project cost approval" },
                        { id: "approvalMeterConnection", l: "To give meter connection after installation" },
                        { id: "approvalFinalPayment", l: "To confirm the final payment received" },
                    ],
                },
                {
                    title: "Messaging & Communications",
                    color: "bg-emerald-500",
                    grid: true,
                    items: [
                        { id: "enableMessaging", l: "Enable System-wide Automated Messaging" },
                    ],
                },
            ];
            */}
        </div>
    );
}
